import {
  collection as collectionMobile,
  FirebaseFirestoreTypes,
  onSnapshot as onSnapshotMobile,
  orderBy as orderByMobile,
  query as queryMobile
} from '@react-native-firebase/firestore';
import { router, useLocalSearchParams, useNavigation } from 'expo-router';
import {
  collection as collectionWeb,
  DocumentData,
  Firestore,
  onSnapshot as onSnapshotWeb,
  orderBy as orderByWeb,
  query as queryWeb,
  QuerySnapshot
} from 'firebase/firestore';
import { useEffect, useLayoutEffect, useState } from 'react';
import { Platform, StyleSheet, View } from 'react-native';

import {
  useDeleteMsgVolunteerEvent,
  useEditMsgVolunteerEvent,
  usePublishMsgVolunteerEvent,
  usePublishResponseIndividualNpo
} from '@/api/hooks/chatRoomsHooks';
import {
  Chat,
  ChatRoom,
  DeleteMessageData,
  EditMessageData,
  NewMessageData,
  ReplyMessageData
} from '@/api/models/chatRoomModels';
import DotsLoader from '@/components/DotsLoader';
import ChatListWithInput from '@/components/messages/ChatListWithInput';
import ChatListWithoutInput from '@/components/messages/ChatListWithoutInput';
import Text from '@/components/Text';
import { useAuth } from '@/contexts/authContext';
import { useEndFlowModal } from '@/contexts/endFlowModalContext';
import { db } from '@/firebaseConfig';
import { firebaseImage, safeJsonParseArray } from '@/utils/firebaseImage';
import { i18n } from '@/utils/i18n';
import { safeJsonParse } from '@/utils/jsonParse';
import { addReplyContext } from '@/utils/chatUtils';
import ChatVolunteerEventRoomHeader from '@/components/messages/ChatVolunteerEventRoomHeader';

const VolunteerEventChat = () => {
  const { state } = useAuth();
  const { showError } = useEndFlowModal();

  const navigation = useNavigation();
  const { chatJson } = useLocalSearchParams<{ chatJson: string }>();
  const chatRoom = safeJsonParse<ChatRoom>(chatJson);

  const { mutateAsync: publishMsg } = usePublishMsgVolunteerEvent();
  const { mutateAsync: editMsg } = useEditMsgVolunteerEvent();
  const { mutateAsync: deleteMsg } = useDeleteMsgVolunteerEvent();
  const { mutateAsync: publishUserNpoResponse } = usePublishResponseIndividualNpo();

  const [chats, setChats] = useState<Chat[]>([]);
  const [isPending, setIsPending] = useState(true);

  useLayoutEffect(() => {
    if (chatRoom) {
      const thumbnail = firebaseImage(safeJsonParseArray(chatRoom.thumbnail ?? '[]').at(0));

      navigation.setOptions({
        headerBackVisible: false,
        headerTitle: () => (
          <ChatVolunteerEventRoomHeader
            thumbnail={thumbnail}
            title={chatRoom.title}
            volunteerId={chatRoom.id ?? ''}
            subtitle={chatRoom.subtitle}
            footer={chatRoom.footer}
          />
        )
      });
    } else {
      showError({ title: 'No chat room' });
      router.back();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onResult = (
    collectionSnapshot:
      | FirebaseFirestoreTypes.QuerySnapshot<FirebaseFirestoreTypes.DocumentData>
      | QuerySnapshot<DocumentData>
  ) => {
    const changes = collectionSnapshot.docChanges();

    if (isPending) setIsPending(false);

    changes.forEach((chat) => {
      if (
        !chats.some((c) => c.id === chat.doc.id) &&
        chat.type === 'added' &&
        chat.newIndex > -1 &&
        chat.oldIndex === -1
      ) {
        chats.push({
          id: chat.doc.id,
          msg: chat.doc.data().msg,
          createdAt: chat.doc.data().createdAt,
          editAt: chat.doc.data().editAt
        });
        setChats([...chats]);
      }

      if (
        chats.some((c) => c.id === chat.doc.id) &&
        chat.type === 'removed' &&
        chat.newIndex === -1 &&
        chat.oldIndex > -1
      ) {
        const indexToDelete = chats.findIndex((c) => c.id === chat.doc.id);
        chats.splice(indexToDelete, 1);
        setChats([...chats]);
      }

      if (
        chats.some((c) => c.id === chat.doc.id) &&
        chat.type === 'modified' &&
        chat.doc.data().msg &&
        chat.newIndex &&
        chat.oldIndex
      ) {
        const indexToUpdate = chats.findIndex((c) => c.id === chat.doc.id);
        const updatedChat = {
          id: chat.doc.id,
          msg: chat.doc.data().msg,
          createdAt: chat.doc.data().createdAt,
          editAt: chat.doc.data().editAt
        };
        chats.splice(indexToUpdate, 1, updatedChat);
        setChats([...chats]);
      }
    });
  };

  const onError = (error: Error) => {
    showError({ title: error.name, message: error.message });
    router.back();
  };

  useEffect(() => {
    if (Platform.OS === 'web') {
      //Web
      const chatsSubscriber = onSnapshotWeb(
        queryWeb(
          collectionWeb(db() as Firestore, 'npoVolunteers', chatRoom?.id ?? '', 'chats'),
          orderByWeb('createdAt', 'asc')
        ),
        onResult,
        onError
      );

      //Stop listening to messages
      return () => chatsSubscriber();
    } else {
      //Mobile
      const chatsSubscriber = onSnapshotMobile(
        queryMobile(
          collectionMobile(db() as FirebaseFirestoreTypes.Module, 'npoVolunteers', chatRoom?.id ?? '', 'chats'),
          orderByMobile('createdAt', 'asc')
        ),
        onResult,
        onError
      );

      //Stop listening to messages
      return () => chatsSubscriber();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onMsgSend = async (data: NewMessageData) => {
    try {
      publishMsg(
        { volunteerId: chatRoom?.id ?? '', message: data.newMessage },
        {
          onError: (e) => {
            console.error(e);
          }
        }
      );
    } catch (e) {
      console.error(e);
    }
  };

  const onMsgEdited = async (data: EditMessageData) => {
    try {
      editMsg(
        {
          volunteerId: data.roomId,
          idMessage: data.idMessage,
          message: data.message
        },
        {
          onError: (e) => {
            console.error(e);
          }
        }
      );
    } catch (e) {
      console.error(e);
    }
  };

  const onMsgDeleted = async (data: DeleteMessageData) => {
    try {
      deleteMsg(
        {
          idMessage: data.idMessage,
          volunteerId: chatRoom?.id ?? ''
        },
        {
          onError: (e) => {
            console.error(e);
          }
        }
      );
    } catch (e) {
      console.error(e);
    }
  };

  const onMsgResponded = async (data: ReplyMessageData) => {
    try {
      publishUserNpoResponse(
        {
          volunteerId: chatRoom?.id ?? '',
          message: addReplyContext(
            {
              repliedMessageFrom: data.repliedMessageFrom,
              repliedMessageFromSubtitle: data.repliedMessageFromSubtitle,
              repliedMessage: data.repliedMessage
            },
            data.newMessage
          ),
          nonProfitOrgId: chatRoom?.nonProfitOrgId ?? ''
        },
        {
          onError: (e) => {
            console.error(e);
          }
        }
      );
      if (chatRoom && chatRoom?.nonProfitOrgId && state.user?.user.id) {
        router.dismissTo({
          pathname: '/messages',
          params: {
            roomId: `${state.user.user.id}#${chatRoom.nonProfitOrgId}`
          }
        });
      } else {
        router.back();
      }
    } catch (e) {
      console.error(e);
    }
  };

  if (!chatRoom || (state.user?.user?.role !== 'INDIVIDUAL' && state.user?.user?.role !== 'NON_PROFIT_ORG')) {
    router.back();
    return;
  }

  if (isPending) {
    return (
      <View style={style.container}>
        <DotsLoader />
      </View>
    );
  }

  if (state.user?.user?.role === 'NON_PROFIT_ORG') {
    return (
      <ChatListWithInput
        chatRoom={chatRoom}
        chats={chats.toReversed()}
        msgSend={onMsgSend}
        msgEdited={onMsgEdited}
        msgDeleted={onMsgDeleted}
      />
    );
  }

  if (!isPending && chats.length === 0) {
    return (
      <View style={style.container}>
        <Text>{i18n.t('messages.no_messages')}</Text>
      </View>
    );
  }

  if (state.user?.user?.role === 'INDIVIDUAL') {
    return (
      <ChatListWithoutInput
        chatRoom={chatRoom}
        chats={chats.toReversed()}
        inverted={false}
        msgResponded={onMsgResponded}
      />
    );
  }
};

const style = StyleSheet.create({
  container: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center'
  }
});

export default VolunteerEventChat;
